import { useState } from 'react'

/**
 * Tela final — solicitação enviada.
 *
 * Faz QUATRO coisas, nesta ordem de importância: dá o número (que a pessoa vai
 * citar ao telefone), diz o que acontece agora, diz o que NÃO precisa ser
 * feito, e — no rodapé — deixa sair daqui.
 *
 * A terceira é a que mais evita ligação. No processo antigo a ficha assinada
 * era exigida logo no começo e travava tudo; quem já conhece o fluxo vai
 * estranhar não ter que assinar nada, e vai ligar para perguntar se faltou
 * algo.
 *
 * A quarta veio depois, do uso real: esta tela também é o que o cookie mostra a
 * quem VOLTA ao link público dias depois, e sem saída ela vira um beco. Ver
 * `SaidaDoBeco`.
 */

/** Para onde a pessoa quer ir depois de soltar este preenchimento. */
export type Destino = 'inicio' | 'acompanhar'

interface Props {
  numero: number | null
  /**
   * Solta este navegador do preenchimento e leva ao destino pedido.
   *
   * Ver `SaidaDoBeco`, abaixo: sem isto esta tela é uma porta que só abre para
   * dentro.
   */
  aoSair: (destino: Destino) => Promise<void>
  prazoAnalise: string | null
  email: string
  /**
   * Vem do `catalogo`, refletindo o parâmetro `ExigirFichaAssinada`.
   *
   * Enquanto a empresa não fecha a decisão, os dois modelos convivem — e a
   * promessa feita aqui tem de bater com o que o processo realmente faz.
   * Dizer "você recebe a ficha para assinar" num modo em que nada é enviado
   * deixa o cliente esperando um e-mail que não existe.
   */
  exigirFicha: boolean
}

export function Concluido({ numero, prazoAnalise, email, exigirFicha, aoSair }: Props) {
  return (
    <div>
      <p className="aviso aviso--ok" role="status">
        Pré-cadastro enviado. Recebemos sua solicitação.
      </p>

      <div className="protocolo">
        <span className="protocolo__rotulo">Número da solicitação</span>
        <strong className="protocolo__numero">{numero ?? '—'}</strong>
        <span className="protocolo__nota">
          Guarde este número: é por ele que localizamos sua solicitação.
        </span>
      </div>

      <h3 className="secao">O que acontece agora</h3>
      <ol className="passos">
        <li>
          Nossa área de Crédito e Cobrança confere os dados e os documentos enviados
          {prazoAnalise ? (
            <>
              , com previsão de retorno até{' '}
              <strong>{new Date(prazoAnalise).toLocaleDateString('pt-BR')}</strong>
            </>
          ) : null}
          .
        </li>
        <li>
          Se faltar alguma coisa, avisamos em <strong>{email}</strong> com exatamente o que precisa
          ser ajustado.
        </li>
        {exigirFicha ? (
          <li>Aprovado o cadastro, você recebe a ficha cadastral para assinar.</li>
        ) : (
          <li>Aprovado o cadastro, avisamos por e-mail e você já pode comprar.</li>
        )}
      </ol>

      <p className="nota">
        {exigirFicha ? (
          <>
            <strong>Você não precisa assinar nada agora.</strong> A ficha só é liberada depois da
            aprovação — e ela não trava a análise.
          </>
        ) : (
          <>
            <strong>Não há ficha para assinar.</strong> A declaração que você aceitou neste
            formulário já vale como sua manifestação — registramos a data, o endereço de origem e a
            identificação do cadastro.
          </>
        )}
      </p>

      <SaidaDoBeco aoSair={aoSair} />
    </div>
  )
}

/**
 * A saída desta tela.
 *
 * O PROBLEMA QUE ELA RESOLVE
 *
 * O navegador guarda um cookie apontando para a solicitação enviada, e ele vale
 * 30 dias. Quem abre o link público de novo — para ver o andamento, para
 * assinar a ficha que chegou por e-mail, para cadastrar outro CNPJ — cai aqui,
 * nesta mesma tela, sem nenhum caminho adiante. A tela certa no minuto seguinte
 * ao envio vira um beco uma semana depois. E a página não consegue apagar o
 * cookie sozinha: ele é HttpOnly, invisível ao JavaScript de propósito.
 *
 * POR QUE DOIS BOTÕES, E NESTA ORDEM
 *
 * Quem volta quer, quase sempre, a área de acompanhamento — é lá que estão o
 * andamento, a decisão e a ficha para assinar. Ela vem primeiro e em destaque.
 * O início fica em segundo porque serve a um caso mais raro: abrir cadastro para
 * outro CNPJ.
 *
 * SEM CONFIRMAÇÃO, ao contrário do atalho do topo. Lá a confirmação existe
 * porque um toque acidental parece ter apagado um preenchimento em andamento;
 * aqui não há preenchimento em andamento — está tudo enviado, o número já foi
 * dado, e nada do que a pessoa fizer a partir daqui muda a solicitação. O texto
 * abaixo diz isso com todas as letras, porque "não perde nada" é a informação
 * que faz alguém apertar o botão em vez de ligar.
 */
function SaidaDoBeco({ aoSair }: { aoSair: (destino: Destino) => Promise<void> }) {
  const [indo, setIndo] = useState<Destino | null>(null)

  function ir(destino: Destino) {
    /* Trava a dupla-clicada: a limpeza é local e instantânea, mas a chamada que
       apaga o cookie leva um instante, e dois pedidos em voo não ajudam. */
    if (indo) return
    setIndo(destino)
    void aoSair(destino)
  }

  return (
    <div className="saida">
      <p className="saida__texto">
        Quer ver o andamento, assinar a ficha ou cadastrar outra empresa? Sua solicitação{' '}
        <strong>continua registrada</strong> — o número acima segue valendo, e nada do que
        você fizer aqui a altera.
      </p>
      <div className="saida__acoes">
        <button
          type="button"
          className="botao botao--principal"
          onClick={() => ir('acompanhar')}
          aria-disabled={indo ? 'true' : undefined}
        >
          {indo === 'acompanhar' ? 'Abrindo…' : 'Acompanhar minha solicitação'}
        </button>
        <button
          type="button"
          className="botao botao--secundario"
          onClick={() => ir('inicio')}
          aria-disabled={indo ? 'true' : undefined}
        >
          {indo === 'inicio' ? 'Abrindo…' : 'Iniciar outro pré-cadastro'}
        </button>
      </div>
    </div>
  )
}
